import React, { useState } from "react";
import "../styles/Pantry.css";
import { FaSort, FaTrashAlt } from "react-icons/fa";

interface ShoppingItem {
  id: string;
  name: string;
  label: string;
}

interface ShoppingListProps {
  items: ShoppingItem[];
  onRemove: (id: string) => void;
  onClearAll: () => void;
}

const groupByLabel = (ingredients: ShoppingItem[]) => {
  const grouped: { [label: string]: ShoppingItem[] } = {};
  ingredients.forEach((item) => {
    if (!grouped[item.label]) {
      grouped[item.label] = [];
    }
    grouped[item.label].push(item);
  });
  return grouped;
};

const getGroup = (label: string) => {
  const l = label[0].toUpperCase();
  if (/[A-H]/.test(l)) return "A–H";
  if (/[I-M]/.test(l)) return "I–M";
  if (/[N-S]/.test(l)) return "N–S";
  return "T–Ö";
};

const ShoppingList: React.FC<ShoppingListProps> = ({
  items,
  onRemove,
  onClearAll,
}) => {
  const [groupedView, setGroupedView] = useState(true);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const groupedItems = groupByLabel(items);
  const sortedItems = [...items].sort((a, b) => a.name.localeCompare(b.name));

  const itemsByGroup: { [group: string]: ShoppingItem[] } = {};
  sortedItems.forEach((item) => {
    const group = getGroup(item.name);
    if (!itemsByGroup[group]) {
      itemsByGroup[group] = [];
    }
    itemsByGroup[group].push(item);
  });

  return (
    <>
      <div className="sorting-button-overlay">
        <FaSort
          className="sort-icon"
          onClick={() => setGroupedView((prev) => !prev)}
          aria-label="Vaihda lajittelua"
          title="Vaihda lajittelua"
        />
        <FaTrashAlt
          className="sort-icon"
          onClick={() => setShowClearConfirm(true)}
          aria-label="Tyhjennä kauppalista"
          title="Tyhjennä kaikki"
        />
        <div className="floating-label">KAUPPALISTA</div>
      </div>

      {groupedView ? (
        <div className="selected-ingredients">
          {Object.entries(groupedItems).map(([label, categoryItems]) => (
            <div key={label} className="ingredient-category">
              <h3 className="category-title">{label}</h3>
              <div className="ingredient-list">
                {categoryItems.map((item) => (
                  <span
                    key={item.id}
                    className={`ingredient-tag ${item.label
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                  >
                    {item.name}
                    <button
                      className="remove-ingredient"
                      onClick={() => onRemove(item.id)}
                    >
                      X
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="selected-ingredients">
          <div className="ingredient-list">
            {sortedItems.map((item) => (
              <span
                key={item.id}
                className={`ingredient-tag ${item.label
                  .toLowerCase()
                  .replace(/\s+/g, "-")}`}
              >
                {item.name}
                <button
                  className="remove-ingredient"
                  onClick={() => onRemove(item.id)}
                >
                  X
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {showClearConfirm && (
        <div className="clear-confirm-modal">
          <p>Tyhjennetäänkö koko kauppalista?</p>
          <div className="modal-buttons">
            <button
              className="confirm"
              onClick={() => {
                onClearAll();
                setShowClearConfirm(false);
              }}
            >
              Tyhjennä
            </button>
            <button
              className="cancel"
              onClick={() => setShowClearConfirm(false)}
            >
              Peruuta
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ShoppingList;
